// /trial/welcome?token=... — verifies magic link, lets user pick a password,
// then forwards them to the existing /signup flow with email + slug pre-filled.
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function TrialWelcome() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, err: "", payload: null });
  // Guard against React.StrictMode double-mount — the magic-link token is
  // one-shot, so the second invocation would 410 ("Link already used") and
  // overwrite the successful first state. This ref ensures the verify call
  // fires exactly once per token per page-load.
  const verifiedOnce = useRef(false);

  useEffect(() => {
    if (verifiedOnce.current) return;
    verifiedOnce.current = true;
    (async () => {
      if (!token) { setState({ loading: false, err: "Missing token in link.", payload: null }); return; }
      try {
        const r = await fetch(`${API}/api/trial/magic-link/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setState({ loading: false, err: d?.detail || `Failed (HTTP ${r.status})`, payload: null }); return; }
        // Stash email + affiliate slug so signup page picks them up
        try {
          sessionStorage.setItem("cr_trial_email", d.email || "");
          if (d.affiliate_slug) sessionStorage.setItem("cr_aff", d.affiliate_slug);
        } catch (_) { /* noop */ }
        setState({ loading: false, err: "", payload: d });
      } catch (e) { setState({ loading: false, err: e?.message || "Network error", payload: null }); }
    })();
  }, [token]);

  const proceed = () => {
    const email = state.payload?.email || "";
    navigate(`/signup?email=${encodeURIComponent(email)}&trial=instant`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-emerald-50 text-slate-900" data-testid="trial-welcome-page">
      <PublicHeader light={true}/>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Card className="p-6">
          {state.loading ? (
            <div className="flex items-center gap-2 text-slate-600"><Loader2 className="w-4 h-4 animate-spin"/> Verifying your link…</div>
          ) : state.err ? (
            <div data-testid="trial-welcome-err">
              <div className="font-bold text-rose-600">Link issue: {state.err}</div>
              <Link to="/trial/start" className="inline-block mt-3 text-sm text-violet-700 underline">Request a fresh link →</Link>
            </div>
          ) : (
            <div data-testid="trial-welcome-ok">
              <div className="flex items-center gap-2 text-emerald-700 font-bold mb-2"><CheckCircle2 className="w-5 h-5"/> Email verified</div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Welcome to Central Rentals, {state.payload.email}</h1>
              <p className="text-sm text-slate-600 mt-2">Your Paid plan is ready to activate. One more tap to set your password and Mark will walk you through the 5-question setup quiz.</p>
              <button onClick={proceed} data-testid="trial-welcome-continue"
                className="inline-flex items-center gap-1.5 mt-5 px-5 py-2.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-bold">
                Activate my trial <ArrowRight className="w-4 h-4"/>
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
